/**
 * Floating Add to Cart Component
 * Shows a sticky add to cart bar when the main add to cart button is out of view
 */

class FloatingAddToCart {
  constructor() {
    this.floatingCart = null;
    this.mainAddToCartContainer = null;
    this.productForm = null;
    this.isVisible = false;
    this.product = null;
    this.currentVariant = null;

    // Config is read in setup() to ensure DOM is ready (script may load async before DOM parses)
    this.scrollThreshold     = 100;
    this.btnLabel            = 'Add to Cart';
    this.btnSoldoutLabel     = 'Sold Out';
    this.btnLoadingLabel     = 'Adding…';
    this.btnSuccessLabel     = 'Added!';
    this.btnPrebookLabel     = 'Prebook at ₹ 1,499';
    this.prebookSalePrice    = '₹ 24,999';
    this.prebookComparePrice = 'MRP. ₹ 29,999';
    this.prebookProductTitle = '';

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Read config here (not in constructor) — DOM is guaranteed ready at DOMContentLoaded
    const cfg = document.getElementById('fatc-config');
    if (cfg) {
      this.scrollThreshold     = parseInt(cfg.dataset.scrollOffset     || '100', 10);
      this.btnLabel            = cfg.dataset.btnLabel            || 'Add to Cart';
      this.btnSoldoutLabel     = cfg.dataset.btnSoldoutLabel     || 'Sold Out';
      this.btnLoadingLabel     = cfg.dataset.btnLoadingLabel     || 'Adding…';
      this.btnSuccessLabel     = cfg.dataset.btnSuccessLabel     || 'Added!';
      this.btnPrebookLabel     = cfg.dataset.btnPrebookLabel     || 'Prebook at ₹ 1,499';
      this.prebookSalePrice     = cfg.dataset.prebookSalePrice    || '₹ 24,999';
      this.prebookComparePrice  = cfg.dataset.prebookComparePrice || 'MRP. ₹ 29,999';
      this.prebookDiscountLabel = cfg.dataset.prebookDiscountLabel || '';
      this.prebookProductTitle  = cfg.dataset.prebookProductTitle  || '';
    }

    // The bar HTML is rendered by sections/floating-add-to-cart.liquid — find it, don't create it.
    this.floatingCart = document.getElementById('floating-add-to-cart-bar');
    if (!this.floatingCart) return; // section not added to this template

    this.productForm = document.querySelector('product-form') ||
                       document.querySelector('form[data-type="add-to-cart-form"]');

    this.getProductData();
    this.bindFloatingCartEvents();
    this.applyInitialPrebookState();
    this.startClaimsAnimation();
    this.setupVariantListener();
    this.setupFormListener();

    if (this.isNesterHomeBaristaProduct()) {
      this.setupTopSectionsObserver();
    } else {
      this.show();
    }
  }

  getProductData() {
    // Get product data from the page
    const productInfoContainer = document.querySelector('[data-product-id]');
    if (productInfoContainer) {
      this.product = {
        id: productInfoContainer.dataset.productId,
        title: document.querySelector('.product__title h1, .product__title h2')?.textContent?.trim() || '',
        url: productInfoContainer.dataset.url || '',
        image: this.getProductImage(),
        price: document.querySelector('.price__regular .price-item--regular')?.textContent?.trim() || '',
        comparePrice: document.querySelector('.price__sale .price-item--sale')?.textContent?.trim() || ''
      };
    }

    // Get current variant
    this.updateCurrentVariant();
  }

  getProductImage() {
    // Try multiple selectors to find product image
    const imageSelectors = [
      '.product__media img',
      '.product-media img',
      '.product__media-item img',
      '.product-single__media img',
      '.product-image img',
      '.featured-image img',
      '.main-product-image img',
      '[data-product-image] img',
      '.product-gallery img',
      '.product__photo img',
      'img[data-product-image-main]'
    ];

    for (const selector of imageSelectors) {
      const img = document.querySelector(selector);
      if (img && img.src) {
        return img.src;
      }
    }

    // Fallback: try to find any image in the product section
    const productSection = document.querySelector('.product, .product-single, [data-section-type="product"]');
    if (productSection) {
      const firstImg = productSection.querySelector('img');
      if (firstImg && firstImg.src) {
        return firstImg.src;
      }
    }

    return '';
  }

  updateCurrentVariant() {
    const variantInput = document.querySelector('.product-variant-id, input[name="id"], select[name="id"]');
    if (variantInput) {
      this.currentVariant = {
        id: variantInput.value,
        available: this.checkProductAvailability(),
        price: document.querySelector('.price__regular .price-item--regular')?.textContent?.trim() || this.product?.price || ''
      };
    }
  }

  checkProductAvailability() {
    // Check multiple indicators for product availability
    const variantInput = document.querySelector('.product-variant-id');
    
    // Method 1: Check if variant input is disabled
    if (variantInput && variantInput.disabled) {
      return false;
    }
    
    // Method 2: Check for sold out button text
    const mainAddButton = document.querySelector('button[name="add"], .btn-product-add, [data-add-to-cart]');
    if (mainAddButton) {
      const buttonText = mainAddButton.textContent?.toLowerCase() || '';
      if (buttonText.includes('sold out') || buttonText.includes('unavailable') || mainAddButton.disabled) {
        return false;
      }
    }
    
    // Method 3: Check for sold out indicators in the page
    const soldOutIndicators = [
      '.sold-out',
      '.product-sold-out',
      '[data-sold-out="true"]',
      '.unavailable',
      '.out-of-stock'
    ];
    
    for (const selector of soldOutIndicators) {
      if (document.querySelector(selector)) {
        return false;
      }
    }
    
    // Method 4: Check product form data
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      const formData = new FormData(productForm);
      const variantId = formData.get('id');
      if (!variantId || variantId === '') {
        return false;
      }
    }
    
    return true;
  }



  setupTopSectionsObserver() {
    // The product info section always has id="shopify-section-main" in the template.
    // Show the floating cart only once the user has scrolled to/past the product section.
    const productSection = document.getElementById('shopify-section-main')
      || document.querySelector('.shopify-section:has(product-form)')
      || document.querySelector('.shopify-section:has(.product-form__submit)');

    if (!productSection) {
      this.show();
      return;
    }

    const checkScroll = () => {
      const rect = productSection.getBoundingClientRect();
      // Show only when the top of the product section has reached or passed the top of the viewport
      if (rect.top <= 10) {
        this.show();
      } else {
        this.hide();
      }
    };

    // Run on load and on every scroll
    checkScroll();
    window.addEventListener('scroll', checkScroll, { passive: true });
    this._scrollHandler = checkScroll;
  }

  applyInitialPrebookState() {
    if (!this.isNesterHomeBaristaProduct()) return;

    // Override button label
    const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
    if (addButton) {
      const span = addButton.querySelector('span');
      if (span) span.textContent = this.btnPrebookLabel;
    }

    // Override prices
    const priceRow = this.floatingCart?.querySelector('#floating-price-row');
    if (priceRow) {
      const saleEl     = priceRow.querySelector('.floating-add-to-cart__sale-price');
      const compareEl  = priceRow.querySelector('.floating-add-to-cart__compare-price');
      const discountEl = priceRow.querySelector('.floating-add-to-cart__discount-badge');
      if (saleEl)     saleEl.textContent    = this.prebookSalePrice;
      if (compareEl)  compareEl.textContent  = this.prebookComparePrice;
      if (discountEl) discountEl.textContent = this.prebookDiscountLabel;
    }
  }

  isNesterHomeBaristaProduct() {
    if (!this.prebookProductTitle) return false;
    const currentTitle = (this.product?.title || '').trim();
    // Supports multiple titles separated by | in the theme editor field
    return this.prebookProductTitle.split('|').map(t => t.trim()).includes(currentTitle);
  }



    getSelectedVariantName() {
    const names = [];

    const checkedInputs = document.querySelectorAll('variant-radios, input[type="radio"]:checked');
    checkedInputs.forEach(input => {
      // Prefer the input's own value over label text — avoids picking up
      // nested label children like swatch images or "Variantor" helper spans.
      const raw = input.value || input.nextElementSibling?.textContent?.trim() || '';
      const cleaned = raw
        .replace(/\s*(sold out|unavailable|variantor)\s*/gi, '')
        .trim();
      if (cleaned && !names.includes(cleaned)) names.push(cleaned);
    });
    if (names.length) return names.join(' / ');

    const selects = document.querySelectorAll('variant-selects select');
    selects.forEach(select => {
      const val = select.options[select.selectedIndex]?.textContent?.trim()
        .replace(/\s*(sold out|unavailable|variantor)\s*/gi, '').trim();
      if (val && !names.includes(val)) names.push(val);
    });
    return names.join(' / ');
  }

  getSalePrice() {
    // The actual selling price (discounted)
    const el = document.querySelector('.price-item--sale.price-item--last') ||
               document.querySelector('.price-item--sale') ||
               document.querySelector('.price__regular .price-item--regular');
    return el?.textContent?.trim() || '';
  }

  getComparePrice() {
    // The original MRP / compare-at price (strikethrough)
    const el = document.querySelector('.price-item--regular-new') ||
               document.querySelector('.variant-item__old-price') ||
               document.querySelector('.price__sale .price-item--regular');
    return el?.textContent?.trim() || '';
  }

  
  getDiscountPercent() {
    // Try to get discount from the page's existing badge
    const discountEl = document.querySelector('.price-item--discount');
    if (discountEl) {
      const text = discountEl.textContent.trim();
      const match = text.match(/(\d+)\s*%/); 
      if (match) return '(' + match[1] + '% OFF)';
    }
    // Fallback: calculate from prices
    const saleText = this.getSalePrice();
    const compareText = this.getComparePrice();
    const saleNum = parseFloat(saleText.replace(/[^\d.]/g, ''));
    const compareNum = parseFloat(compareText.replace(/[^\d.]/g, ''));
    if (saleNum && compareNum && compareNum > saleNum) {
      const pct = Math.round(((compareNum - saleNum) / compareNum) * 100);
      return '(' + pct + '% OFF)';
    }
    return '';
  }



  getCurrentVariantImage() {
    // Method 1: Get the currently selected variant image from checked radio input
    const selectedVariantInput = document.querySelector('input[type="radio"]:checked[name*="option"], input[type="radio"]:checked[name*="Option"]');
    if (selectedVariantInput) {
      const selectedVariantSwatch = selectedVariantInput.nextElementSibling?.querySelector('.variant-image-swatch');
      if (selectedVariantSwatch) {
        return selectedVariantSwatch.src;
      }
    }

    // Method 2: Try alternative selector structure
    const selectedVariantSwatch = document.querySelector('input[type="radio"]:checked + .variant-image-swatch-label .variant-image-swatch');
    if (selectedVariantSwatch) {
      return selectedVariantSwatch.src;
    }

    // Method 3: Get from variant dropdown selection
    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect && variantSelect.value) {
      const selectedVariantId = variantSelect.value;
      // Find the variant image for this variant ID
      const variantImageElement = document.querySelector(`[data-variant-id="${selectedVariantId}"] .variant-image-swatch, [data-variant="${selectedVariantId}"] .variant-image-swatch`);
      if (variantImageElement) {
        return variantImageElement.src;
      }
    }

    // Method 4: Get from current variant data
    if (this.currentVariant && this.currentVariant.id) {
      const variantImageElement = document.querySelector(`[data-variant-id="${this.currentVariant.id}"] .variant-image-swatch`);
      if (variantImageElement) {
        return variantImageElement.src;
      }
    }
    
    // Fallback to first variant image or product image
    const firstVariantSwatch = document.querySelector('.variant-image-swatch');
    if (firstVariantSwatch) {
      return firstVariantSwatch.src;
    }
    
    return this.product?.image || '';
  }

  bindFloatingCartEvents() {
    const addButton = this.floatingCart.querySelector('#floating-add-to-cart-btn');
    addButton?.addEventListener('click', () => this.handleAddToCart());
  }

  show() {
    if (this.floatingCart) {
      requestAnimationFrame(() => this.floatingCart.classList.add('visible'));
      this.isVisible = true;
    }
  }

  hide() {
    if (this.floatingCart) {
      this.floatingCart.classList.remove('visible');
      this.isVisible = false;
    }
  }

  setupVariantListener() {
    // Fires after product-info.js resolves the variant — price/availability/image are accurate
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChangeEvent.bind(this));
    }
  }

  handleVariantChangeEvent(event) {
    const variant = event.data?.variant;
    if (!variant) return;

    // --- Update button state ---
    const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
    if (addButton) {
      const available = variant.available;
      addButton.disabled = !available;
      addButton.querySelector('span').textContent = this.isNesterHomeBaristaProduct()
        ? this.btnPrebookLabel
        : (available ? this.btnLabel : this.btnSoldoutLabel);
      addButton.classList.toggle('disabled', !available);
    }

    // --- Update variant name ---
    const variantNameEl = this.floatingCart?.querySelector('#floating-variant-name');
    if (variantNameEl) variantNameEl.textContent = this.getSelectedVariantName();

    // --- Update price directly from variant object (avoids stale DOM reads) ---
    const priceRow = this.floatingCart?.querySelector('#floating-price-row');
    if (priceRow) {
      const saleEl     = priceRow.querySelector('.floating-add-to-cart__sale-price');
      const compareEl  = priceRow.querySelector('.floating-add-to-cart__compare-price');
      const discountEl = priceRow.querySelector('.floating-add-to-cart__discount-badge');

      if (this.isNesterHomeBaristaProduct()) {
        if (saleEl)     saleEl.textContent    = this.prebookSalePrice;
        if (compareEl)  compareEl.textContent  = this.prebookComparePrice;
        if (discountEl) discountEl.textContent = this.prebookDiscountLabel;
      } else {
        const formatMoney = (cents) => {
          if (!cents) return '';
          return '\u20B9' + (cents / 100).toLocaleString('en-IN');
        };

        const salePrice    = formatMoney(variant.price);
        const comparePrice = variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price)
          : '';
        const discountPct  = (comparePrice && variant.price && variant.compare_at_price > variant.price)
          ? '(' + Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100) + '% OFF)'
          : '';

        if (saleEl)     saleEl.textContent    = salePrice;
        if (compareEl)  compareEl.textContent  = comparePrice ? 'MRP: ' + comparePrice : '';
        if (discountEl) discountEl.textContent = discountPct;
      }
    }

    // --- Update thumbnail image ---
    const imgEl = this.floatingCart?.querySelector('.floating-add-to-cart__product-image img');
    if (imgEl) {
      // Use variant's featured_image if available, else fall back to swatch/page image
      const featuredImage = variant.featured_image?.src;
      const newSrc = featuredImage
        ? featuredImage
        : this.getCurrentVariantImage();
      if (newSrc) imgEl.src = newSrc;
    }
  }



  setupFormListener() {
    // Listen for successful form submissions - fire showSuccessState only once per add action
    const cartSuccessEvents = ['cart:item-added', 'product:added-to-cart', 'cart:updated', 'cart:refresh'];
    cartSuccessEvents.forEach(eventName => {
      document.addEventListener(eventName, () => {
        // Only act if we are currently in a loading state (i.e. the floating ATC initiated the add)
        const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
        if (addButton?.classList.contains('loading')) {
          this.showSuccessState();
        }
      });
    });
  }

  handleAddToCart() {
    const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
    if (!addButton || addButton.disabled) return;
    
    // Show loading state
    this.showLoadingState();
    
    // Submit main form
    const mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (mainForm) {
      const submitButton = mainForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.click();
      }
    }

    // Fallback: reset button state if no cart event fires within 5 seconds
    this._atcFallbackTimer = setTimeout(() => {
      this.resetButtonState();
    }, 5000);
  }

  showLoadingState() {
    const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
    if (addButton) {
      addButton.classList.add('loading');
      addButton.disabled = true;
      addButton.innerHTML = `
        <div class="loading-spinner"></div>
        <span>${this.btnLoadingLabel}</span>
      `;
    }
  }

  showSuccessState() {
    // Clear the fallback timer since the add succeeded
    clearTimeout(this._atcFallbackTimer);

    const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
    if (!addButton || addButton.classList.contains('success')) return; // already showing success

    addButton.classList.remove('loading');
    addButton.classList.add('success');
    addButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${this.btnSuccessLabel}</span>
    `;

    setTimeout(() => {
      this.resetButtonState();
    }, 2000);
  }

  resetButtonState() {
    const addButton = this.floatingCart?.querySelector('#floating-add-to-cart-btn');
    if (addButton) {
      addButton.classList.remove('loading', 'success');
      addButton.disabled = false;
      const label = this.isNesterHomeBaristaProduct() ? this.btnPrebookLabel : this.btnLabel;
      addButton.innerHTML = `<span>${label}</span>`;
    }
  }

  startClaimsAnimation() {
    const claimsRow   = this.floatingCart?.querySelector('#floating-atc-claims-row');
    const claimsNumEl = this.floatingCart?.querySelector('#floating-atc-claims-num');
    if (!claimsNumEl) return;

    const counter = window.NesterClaimsCounter;
    if (!counter) return;

    const config = document.getElementById('fatc-config');
    const showClaims = config ? config.dataset.showClaims !== 'false' : true;
    if (!showClaims) return;

      // Only show counter for Nester Home Cook Air Fryer
    const currentTitle = (this.product?.title || '').trim();
    if (currentTitle !== 'Nester Home Cook Air Fryer') return;

    const pad = n => n < 10 ? '0' + n : String(n);
    claimsNumEl.textContent = pad(counter.getClaimed());
    if (claimsRow) claimsRow.style.display = '';

    // Register tick listener — update when shared counter increments
    if (this._claimsTickCb) counter.offTick(this._claimsTickCb);
    this._claimsTickCb = (claimed) => {
      claimsNumEl.textContent = pad(claimed);
      claimsNumEl.classList.remove('floating-atc-claims-pulse');
      void claimsNumEl.offsetWidth;
      claimsNumEl.classList.add('floating-atc-claims-pulse');
    };
    counter.onTick(this._claimsTickCb);
  }

  // Public method to refresh the floating cart data
  refresh() {
    this.getProductData();
  }
}

// Initialize when script loads
const floatingCartInstance = new FloatingAddToCart();

// Store instance reference for access
document.addEventListener('DOMContentLoaded', () => {
  const floatingCartElement = document.querySelector('.floating-add-to-cart');
  if (floatingCartElement) {
    floatingCartElement._floatingCart = floatingCartInstance;
  }
});

// Export for potential external use
window.FloatingAddToCart = FloatingAddToCart;
window.floatingCartInstance = floatingCartInstance;
